import React from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { createSelector } from 'reselect';
import AppFunctionsService from '../../../services/appFunctionServices';
import { IPhenologyAssignment } from '../../../types/model/phenology/assignment';
import lodash from 'lodash';
import { MapBoxRef } from '../../customComponents/mapBox/MapBox';
import GeoHelper from '../../../services/helper/geoHelper';
import BasicMap from '../../customComponents/mapBox/BasicMap';
import ScoutingAssignmentFinishedMarkers from '../../customComponents/mapBox/layer/scouting/FinishedPointsLayer';
import PhenologyAssignmentLocationHistoryMarkers from '../../customComponents/mapBox/layer/phenology/LocationHistoryLayer';
import MapboxBlockPointLayer from '../../customComponents/mapBox/layer/masterData/PointLayer';
import { IGeoServerBlock } from '../../../types/model/masterData/geoserver/block';
import { IRootState } from '../../../@types/redux';
import { connect } from 'react-redux';

interface IPhenologyAssignmentInfoViewProps {
    value : IPhenologyAssignment;
    geoserverblocks : Array<IGeoServerBlock>;
}

interface IPhenologyAssignmentInfoViewState {}

class PhenologyAssignmentInfoViewComponent extends React.PureComponent<IPhenologyAssignmentInfoViewProps, IPhenologyAssignmentInfoViewState> {

    constructor(props : IPhenologyAssignmentInfoViewProps) {
        super(props);
        this.state = {};
    }

    private readonly getBlocks = (props : IPhenologyAssignmentInfoViewProps) => props.geoserverblocks;
    private readonly getScoutingBlock = (props : IPhenologyAssignmentInfoViewProps) => props.value.scoutingBlock;
    private readonly getFinished = (props : IPhenologyAssignmentInfoViewProps) => props.value.finishedPoints;
    
    private readonly getFinishedPoints = createSelector(
        [this.getFinished, this.getScoutingBlock],
        (finishedPoints, block) => {            
            return lodash
                .chain(finishedPoints)
                .map(x => x)
                .flatMap(x => x)
                .map(n => block.phenologyPoints.features.filter(x => x.properties.guid === n))
                .flatMap()
                .map(x => x.geometry.coordinates)
                .value();
        },
    );

    private readonly getBlock : (props : IPhenologyAssignmentInfoViewProps) => IGeoServerBlock | null = createSelector(
        [this.getBlocks, this.getScoutingBlock],
        (blocks, block) => {
            return lodash
                .filter(blocks, (geoserverBlock) => (block.division.toLocaleLowerCase() === geoserverBlock.departmentShortName.toLocaleLowerCase()))
                .filter((geoserverBlock) => (block.landName.toLocaleLowerCase() === geoserverBlock.fieldName.toLocaleLowerCase()))
                .filter((geoserverBlock) => (block.name.toLocaleLowerCase() === geoserverBlock.code.toLocaleLowerCase()))[0];
        },
    );

    private readonly getBlockGuid = createSelector(
        [this.getBlock],
        (block) => {
            return block?.guid;
        },
    );

    private readonly setMapRef = (mapRef : MapBoxRef) => {
        const { value } = this.props;

        mapRef.fitBounds(GeoHelper.getAssignmentBlockBounds([value.scoutingBlock]), {
            minZoom: 15,
            animate: false,
        });
    };

    public readonly render = () => {
        const { value } = this.props;

        const blockGuid = this.getBlockGuid(this.props);
        const finishedPoints = this.getFinishedPoints(this.props);

        return (
            <div className='fdr flx1 ais'>
                <div className='fdc flx1 ais mr5'>
                    <AppBar className='fdc ais' variant='outlined' position='static'>
                        <Toolbar variant='dense' className={'fdr aic jcc'}>
                            <Typography className={'cw'} variant='h6'>
                            BLOCK - {value.blockName}
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <div className='fdc flx1'>
                        <BasicMap
                            id={'phenologfyAssignmentMap'}
                            onMap={this.setMapRef}
                        >
                            <MapboxBlockPointLayer
                                pointsId='point_farm_layer'
                                minZoom={15}
                                type='phenology'
                                blockGuids={!blockGuid ? [] : [blockGuid]}
                            />
                            <PhenologyAssignmentLocationHistoryMarkers
                                polygonId={'point_history_layer'}
                                minZoom={15}
                                assignmentId={value.id}
                            />
                            <ScoutingAssignmentFinishedMarkers
                                polygonId={'point_finished_layer'}
                                minZoom={15}
                                finishedPoints={finishedPoints}
                            />
                        </BasicMap>
                    </div>
                </div>
                <div className='fdc flx1 ais ml5'>
                    <AppBar className='fdc ais' variant='outlined' position='static'>
                        <Toolbar variant='dense' className={'fdr aic jcc'}>
                            <Typography className={'cw'} variant='h6'>
                            ASSIGNMENT
                            </Typography>
                        </Toolbar>
                    </AppBar>
                    <div className='fdr mt5'>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                            DATE
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {AppFunctionsService.formatDateTimeToDateOnly(value.date)}
                            </Typography>
                        </div>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                            COMPLETED POINTS
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {`${Object.values(value.finishedPoints).flat().length} / ${value.scoutingBlock.phenologyPointsArray.length * value.directions.length}`}
                            </Typography>
                        </div>
                    </div>
                    <div className='fdr mt5'>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                                SCOUT
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {`${value.employeeNumber} - ${value.employeeName}`}
                            </Typography>
                        </div>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                            DIVISION
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {value.division.toLocaleUpperCase()}
                            </Typography>
                        </div>
                    </div>
                    <div className='fdr mt5'>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                            LAND NAME
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {value.landName.toLocaleUpperCase()}
                            </Typography>
                        </div>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                            BLOCK NAME
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {value.blockName.toLocaleUpperCase()}
                            </Typography>
                        </div>
                    </div>
                    <div className='fdr mt5'>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                            LAND DESCRIPTION
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {value.scoutingBlock.description.toLocaleUpperCase()}
                            </Typography>
                        </div>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                            CROP
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {value.crop.toLocaleUpperCase()}
                            </Typography>
                        </div>
                    </div>
                    <div className='fdr mt5'>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                            AGE
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {value.scoutingBlock.age.toFixed(0)}
                            </Typography>
                        </div>
                        <div className='fdc flx1'>
                            <Typography className={'flx1 fw600 bbw1 bocg pl10 pr10'} variant='subtitle1'>
                            AREA
                            </Typography>
                            <Typography className={'flx1 pl10 pr10 mnh48'} variant='subtitle1'>
                                {
                                    !value.scoutingBlock.ha &&
                                    '-'
                                }
                                {
                                    !!value.scoutingBlock.ha &&
                                    `${value.scoutingBlock.ha.toFixed(2)} Ha`
                                }
                            </Typography>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        geoserverblocks: state.masterData.geoserver.blocks,
    };
};

const PhenologyAssignmentInfoView = connect(
    mapStateToProps,
)(PhenologyAssignmentInfoViewComponent);


export default PhenologyAssignmentInfoView;